import React, { useContext, useMemo, useState } from "react";
import { RotateCcw, Send } from "lucide-react";
import TableGrid from "modules/swiftboard/components/TableGrid";
import TableToolBar from "modules/swiftboard/components/TableToolBar";
import Pagination from "modules/swiftboard/components/Pagination";
import NoDataState from "modules/swiftboard/components/NoDataState";
import { GlobalAdvanceFilter } from "modules/swiftboard/components/advance-filter";
import ConfirmDialogue from "components/ui/ConfirmDialogue";
import { useAlert } from "context/AlertContext";
import { AuthContext } from "context/AuthContext";
import useConfirm from "../../components/useConfirm";
import useServerList from "../../components/useServerList";
import { MANAGE_ROLE } from "../../features/builder.constants";
import { errorMessage } from "../../features/builder.utils";
import StatTiles from "../components/StatTiles";
import useStats from "../components/useStats";
import { CHANNEL_LABEL } from "../features/monitor.constants";
import apiReminderDelivery from "./api";
import { loadDeliveryFilterDependencies } from "./features/delivery.dependencies";
import { deliveryFilterFields } from "./features/delivery.filter";
import { getDeliveryColumns } from "./delivery-list/deliveryColumns";
import DeliveryRowActions from "./delivery-list/DeliveryRowActions";
import DeliveryViewDrawer from "./delivery-dialogs/DeliveryViewDrawer";

// Reminder Monitor -> Deliveries (tbl_rem_delivery): every message the engine sent, skipped or failed.
const Delivery = () => {
  const { showAlert } = useAlert();
  const { user } = useContext(AuthContext);
  const canManage = user?.user_role?.role === MANAGE_ROLE;
  const list = useServerList(apiReminderDelivery.getAll, { loadError: "Failed to load reminder messages.", initialSort: { key: "id", direction: "desc" } });
  const { stats, loading: statsLoading } = useStats(apiReminderDelivery.getStats, list.refreshKey);
  const { confirmData, openConfirm, handleConfirmClose } = useConfirm();
  const [view, setView] = useState(null);
  const [busy, setBusy] = useState(null);

  // ── Handlers ──────────────────────────────────────────────────────────────

  const openView = async (row) => {
    setBusy({ id: row.id, action: "view" });
    try {
      const res = await apiReminderDelivery.getById(row.id);
      setView(res.data?.data ?? null);
    } catch (err) {
      showAlert({ type: "error", message: errorMessage(err) });
    } finally {
      setBusy(null);
    }
  };

  const handleRetry = async (ids) => {
    const confirmed = await openConfirm({
      title: "Send Again",
      message: `Send ${ids.length > 1 ? `${ids.length} failed messages` : "this failed message"} again, with the content rendered at the time? Messages that did not fail are left as they are.`,
      confirmText: "Send again",
      variant: "primary",
      icon: <RotateCcw size={20} />,
    });
    if (!confirmed) return;
    setBusy(ids.length === 1 ? { id: ids[0], action: "retry" } : null);
    try {
      const res = await apiReminderDelivery.retry(ids);
      const outcome = res.data?.data ?? {};
      showAlert({ type: outcome.failed ? "warning" : "success", message: res.data?.message || "Done." });
      list.resetSelection();
      list.reload();
    } catch (err) {
      showAlert({ type: "error", message: errorMessage(err) });
    } finally {
      setBusy(null);
    }
  };

  // ── Columns ───────────────────────────────────────────────────────────────

  const columns = useMemo(() => getDeliveryColumns(list.start), [list.start]);
  const s = stats?.by_status ?? {};
  const tiles = [
    { label: "Sent", value: s.SENT, tone: "success" },
    { label: "Failed", value: s.FAILED, tone: s.FAILED ? "danger" : "muted", hint: s.FAILED ? "Select them to send again" : undefined },
    { label: "Skipped", value: s.SKIPPED, tone: "muted", hint: "Opted out, no address, day off..." },
    { label: "Queued", value: s.QUEUED, tone: "muted" },
  ];
  const channelNote = (stats?.by_channel ?? []).map((c) => `${CHANNEL_LABEL[c.channel_key] ?? c.channel_key}: ${c.sent} sent${c.failed ? `, ${c.failed} failed` : ""}`).join(" · ");

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="h-full overflow-y-auto">
      <div className="space-y-4 pb-4">
        <StatTiles items={tiles} loading={statsLoading} note={stats ? `${stats.from} to ${stats.to} (${stats.timezone})${channelNote ? ` · ${channelNote}` : ""}` : null} />
        <TableToolBar
          onSearch={list.handleSearch}
          onRefresh={list.handleRefresh}
          resetSearchTrigger={list.resetTrigger}
          canCreate={false}
          selectedCount={list.selectedRows.length}
          bulkActions={canManage ? [{ key: "retry", label: `Send again (${list.selectedRows.length})`, icon: RotateCcw, variant: "primary", onClick: () => handleRetry(list.selectedRows) }] : undefined}
          onClearSelection={list.resetSelection}
          resultCount={list.totalCount}
          advanceFilter={<GlobalAdvanceFilter fields={deliveryFilterFields} dependenciesLoader={loadDeliveryFilterDependencies} onApply={list.handleApplyFilters} resetKey={list.resetTrigger} />}
        />

        {!list.loading && list.records.length === 0 ? (
          <div className="rounded-lg border border-border bg-surface">
            <NoDataState
              icon={Send}
              title={list.hasQuery ? "No messages match" : "No reminder messages yet"}
              message={list.hasQuery ? "No messages match your search or filters. Try adjusting them." : "Every email and in-app reminder the engine sends (or skips, and why) is listed here."}
              showBack={false}
              showRefresh
              onRefresh={list.handleRefresh}
            />
          </div>
        ) : (
          <>
            <div className="overflow-hidden rounded-lg border border-border">
              <TableGrid
                data={list.records}
                loading={list.loading}
                columns={columns}
                pageSize={list.query.size}
                sortConfig={list.query.sort}
                onSort={list.handleSort}
                onSelectionChange={list.setSelectedRows}
                resetSelectionTrigger={list.selectionReset}
                selectable={canManage}
                showActions
                renderActions={(row) => <DeliveryRowActions row={row} canManage={canManage} busy={busy} onView={openView} onRetry={handleRetry} />}
              />
            </div>
            <Pagination totalCount={list.totalCount} pageSize={list.query.size} currentPage={list.query.page} onPageChange={list.handlePageChange} selectedCount={list.selectedRows.length} />
          </>
        )}
      </div>

      {view && <DeliveryViewDrawer record={view} onClose={() => setView(null)} />}
      {confirmData.isOpen && <ConfirmDialogue confirmData={confirmData} handleConfirmClose={handleConfirmClose} />}
    </div>
  );
};

export default Delivery;
